<script setup>
import { computed, onMounted, ref } from 'vue'
import { api } from '../services/api'
import { store } from '../store'

const data = ref(null)
const error = ref('')
const weekday = new Intl.DateTimeFormat('vi-VN', { weekday: 'long' }).format(new Date())
const nextLesson = computed(() => data.value?.continue_learning[0])
const otherLessons = computed(() => data.value?.continue_learning.slice(1) || [])

async function load() {
  try {
    data.value = await api('/dashboard')
  } catch (err) {
    error.value = err.message
  }
}

onMounted(load)
</script>

<template>
  <div v-if="error" class="error-banner">{{ error }} <button class="btn btn-light" @click="load">Thử lại</button></div>
  <div v-if="!data" class="loading-block"><div><div class="loader"></div><p>Đang tải góc học tập…</p></div></div>
  <template v-else>
    <section class="hero today-hero">
      <div class="hero-copy">
        <p class="eyebrow">{{ weekday }} · Góc học tập của em</p>
        <h1>{{ data.greeting }}! <span class="hero-wave">✦</span></h1>
        <div class="today-pills">
          <span>🔥 {{ data.stats.streak_days }} ngày liên tiếp</span>
          <span>✓ {{ data.stats.completed_lessons }} bài đã xong</span>
          <span>◷ {{ data.stats.study_minutes }} phút học</span>
        </div>
      </div>
      <RouterLink v-if="nextLesson" :to="`/bai-hoc/${nextLesson.id}`" class="today-next">
        <small>{{ nextLesson.progress ? 'Học tiếp bài đang dở' : 'Bài tiếp theo của em' }} · {{ nextLesson.subject_title }}</small>
        <strong>{{ nextLesson.title }}</strong>
        <span class="today-next-meta">{{ nextLesson.chapter_title }} · {{ nextLesson.duration_minutes }} phút</span>
        <div v-if="nextLesson.progress" class="progress-track"><span :style="{ width: `${nextLesson.progress}%` }"></span></div>
        <b class="btn btn-yellow">{{ nextLesson.progress ? 'Học tiếp' : 'Bắt đầu học' }} →</b>
      </RouterLink>
      <div v-else class="today-next">
        <small>{{ data.subjects.length ? 'Tuyệt vời!' : `Lớp ${store.user.grade}` }}</small>
        <strong>{{ data.subjects.length ? 'Em đã học hết các bài hiện có' : 'Chưa có học liệu cho lớp của em' }}</strong>
        <span class="today-next-meta">{{ data.subjects.length ? 'Chọn một môn để ôn lại và luyện thêm.' : 'Bài học sẽ xuất hiện khi môn học được mở.' }}</span>
        <RouterLink v-if="data.subjects.length" to="/mon-hoc" class="btn btn-yellow">Ôn tập theo môn →</RouterLink>
      </div>
    </section>

    <div class="dashboard-grid">
      <section>
        <div class="section-heading"><div><h2>Môn học của em</h2><p>Bấm vào môn để xem các chương và bài</p></div><RouterLink class="text-link" to="/mon-hoc">Tất cả môn →</RouterLink></div>
        <div class="today-subjects">
          <RouterLink v-for="subject in data.subjects" :key="subject.id" :to="`/mon-hoc/${subject.slug}`" class="card today-subject" :class="subject.color">
            <span class="subject-icon" :class="subject.color">{{ subject.icon }}</span>
            <div><strong>{{ subject.title }}</strong><small>{{ subject.progress.completed }}/{{ subject.progress.total }} bài · {{ subject.progress.percent }}%</small><div class="progress-track"><span :style="{ width: `${subject.progress.percent}%` }"></span></div></div>
          </RouterLink>
        </div>
        <template v-if="otherLessons.length">
          <div class="section-heading"><div><h2>Bài đang học dở</h2><p>Quay lại hoàn thành để không quên kiến thức</p></div></div>
          <div class="course-list">
            <RouterLink v-for="lesson in otherLessons" :key="lesson.id" :to="`/bai-hoc/${lesson.id}`" class="card continue-card compact">
              <div><small class="continue-subject">{{ lesson.subject_title }}</small><h3>{{ lesson.title }}</h3><div class="progress-track"><span :style="{ width: `${lesson.progress}%` }"></span></div></div>
              <span class="text-link">Học tiếp {{ lesson.progress }}% →</span>
            </RouterLink>
          </div>
        </template>
      </section>

      <aside>
        <div class="section-heading"><div><h2>Cần ôn lại</h2><p>Dựa trên bài em đã làm</p></div></div>
        <div class="card recommend-card">
          <div v-if="!data.weak_topics.length" class="weak-item"><div class="weak-top"><b>Chưa có đủ dữ liệu</b><span>—</span></div><small>Làm thêm bài luyện tập để hệ thống gợi ý chủ đề cần ôn.</small></div>
          <RouterLink v-for="topic in data.weak_topics" :key="topic.chapter_id" :to="`/mon-hoc/${topic.subject_slug}`" class="weak-item weak-link">
            <div class="weak-top"><b>{{ topic.title }}</b><span>{{ topic.accuracy }}% đúng</span></div>
            <div class="progress-track"><span :style="{ width: `${topic.accuracy}%`, background: topic.accuracy < 60 ? '#e8894f' : '#dfb43f' }"></span></div>
            <small>{{ topic.subject }} · Ôn lại →</small>
          </RouterLink>
          <RouterLink to="/tien-do" class="btn btn-light" style="width:100%; margin-top:10px">Xem tiến độ chi tiết</RouterLink>
        </div>
      </aside>
    </div>
  </template>
</template>
